import { useMemo, useState } from 'react';
import { Chip, ListItemButton } from '@mui/material';
import { CompanyDailyNavConfigVersion } from 'api';
import {
  dailyNavDateLabel,
  dailyNavNextDayLabel,
  formatDailyNavEffectivePeriod,
  fractionToPercent,
  getDailyNavThresholdGroups,
} from 'common/constants/dailyNav';
import { COMP_TYPE_LABELS } from 'common/constants/dailyNavComps';
import { Box, Button, Divider, List, ListItem, ListItemText, Stack, Text } from 'components/ui';
import { PrismThemeProvider } from 'theme';
import { DailyNavCompanyVersionHistoryProps } from './DailyNavCompanyVersionHistory.props';
import { DAILY_NAV_COMPANY_VERSION_HISTORY_TEST_IDS as TEST_IDS } from './DailyNavCompanyVersionHistory.testIds';

const CHANGE_TRIGGER_LABELS: Record<CompanyDailyNavConfigVersion.change_trigger, string> = {
  [CompanyDailyNavConfigVersion.change_trigger.FIRM_TEMPLATE_CHANGE]: 'Firm template change',
  [CompanyDailyNavConfigVersion.change_trigger.COMPANY_EDIT]: 'Company edit',
};

// Company config versions with in-effect chips and a values-in-effect detail.
// Shared by the company Daily NAV Settings tab and the setup wizard's context panel.
const DailyNavCompanyVersionHistory = ({
  history,
  hasMoreHistory,
  isLoadingMoreHistory,
  loadMoreHistory,
  anchoredCompanyVersionId,
  hasDivergence,
  governingNavDate,
  nextNavDate,
  renderActiveVersionAction,
}: DailyNavCompanyVersionHistoryProps) => {
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const selectedVersion = useMemo(
    () => history?.find(version => version.id === selectedVersionId) ?? null,
    [history, selectedVersionId]
  );

  // Rendered on pages without the Prism theme, which components/ui needs.
  return (
    <PrismThemeProvider>
      <Text variant="h4" gutterBottom>
        Version History
      </Text>
      {history?.length ? (
        <List dense>
          {history.map(version => (
            /* secondaryAction keeps the action out of ListItemButton so the trailing
                 control isn't nested inside the row's own interactive element. */
            <ListItem
              key={version.id}
              disablePadding
              secondaryAction={version.is_active ? renderActiveVersionAction?.(version) : undefined}>
              <ListItemButton
                selected={version.id === selectedVersionId}
                data-testid={TEST_IDS.versionHistoryItem(version.id)}
                onClick={() => setSelectedVersionId(version.id === selectedVersionId ? null : (version.id ?? null))}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <span>{formatDailyNavEffectivePeriod(version)}</span>
                      {/* The COMPANY anchor, not the firm one: separate id spaces. */}
                      {version.id === anchoredCompanyVersionId && (
                        <Chip
                          size="small"
                          color={hasDivergence ? 'warning' : 'success'}
                          label={
                            hasDivergence
                              ? `In effect for ${dailyNavDateLabel(governingNavDate)} NAV`
                              : 'Current — today’s NAV and the next'
                          }
                          data-testid={TEST_IDS.versionHistoryCurrentChip(version.id)}
                        />
                      )}
                      {hasDivergence && version.is_active && version.id !== anchoredCompanyVersionId && (
                        <Chip size="small" color="info" label={dailyNavNextDayLabel(nextNavDate)} />
                      )}
                      {anchoredCompanyVersionId === null && version.is_active && (
                        <Chip
                          size="small"
                          color="success"
                          label="Current"
                          data-testid={TEST_IDS.versionHistoryCurrentChip(version.id)}
                        />
                      )}
                    </Stack>
                  }
                  secondary={[
                    version.created_by_name ? `Changed by ${version.created_by_name}` : null,
                    CHANGE_TRIGGER_LABELS[version.change_trigger],
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      ) : (
        <Text variant="body2">No configuration changes have been saved yet.</Text>
      )}
      {hasMoreHistory && (
        <Button
          fullWidth
          size="small"
          onClick={() => loadMoreHistory()}
          disabled={isLoadingMoreHistory}
          data-testid={TEST_IDS.versionHistoryLoadMore}>
          {isLoadingMoreHistory ? 'Loading...' : 'Load More'}
        </Button>
      )}
      {selectedVersion && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Text variant="body2" fontWeight="bold" gutterBottom>
            Values in effect {formatDailyNavEffectivePeriod(selectedVersion)}
          </Text>
          {selectedVersion.firm_config_version && (
            <Text variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Anchored to the firm config effective {formatDailyNavEffectivePeriod(selectedVersion.firm_config_version)}
            </Text>
          )}
          {/* That version's own anchor, so history shows the windows then. */}
          {getDailyNavThresholdGroups(
            selectedVersion.firm_config_version?.short_window_sessions,
            selectedVersion.firm_config_version?.long_window_sessions
          ).map(group => (
            <Box key={group.title} sx={{ mb: 1 }}>
              <Text variant="caption" fontWeight="bold" color="text.secondary">
                {group.title.toUpperCase()}
              </Text>
              {group.fields.map(field => {
                const overrideValue = selectedVersion[field.key];
                const firmValue = selectedVersion.firm_config_version?.[field.key];
                const displayValue = fractionToPercent(overrideValue) || fractionToPercent(firmValue) || '—';

                return (
                  <Text key={field.key} variant="body2">
                    {field.label}: {displayValue}%{' '}
                    {overrideValue === null || overrideValue === undefined ? '(inherited)' : '(override)'}
                  </Text>
                );
              })}
            </Box>
          ))}
          <Box sx={{ mt: 1 }}>
            <Text variant="caption" fontWeight="bold" color="text.secondary">
              COMPS WATCHLIST
            </Text>
            {selectedVersion.comps?.length ? (
              <List dense data-testid={TEST_IDS.historyCompsList(selectedVersion.id)}>
                {selectedVersion.comps.map(comp => (
                  <ListItemText
                    key={comp.cap_iq_id}
                    primary={`${comp.ticker_symbol} — ${comp.company_name}`}
                    secondary={COMP_TYPE_LABELS[comp.comp_type]}
                  />
                ))}
              </List>
            ) : (
              <Text
                variant="body2"
                color="text.secondary"
                data-testid={TEST_IDS.historyNoCompsHint(selectedVersion.id)}>
                No comps
              </Text>
            )}
          </Box>
        </Box>
      )}
    </PrismThemeProvider>
  );
};

export default DailyNavCompanyVersionHistory;
